import { toast as sonnerToast } from "sonner";

import { icons as baseIcons } from "~/ui/common";
import { tw } from "~/utils";

const icons = baseIcons.solid;

type ToastType = "success" | "error" | "warning";

interface ToastProps {
  id: string | number;
  title: string;
  description?: string;
  type: ToastType;
  button?: {
    label: string;
    onClick: () => void;
  };
}

export const toast = (toast: Omit<ToastProps, "id">) => {
  return sonnerToast.custom(
    (id) => (
      <Toast
        id={id}
        title={toast.title}
        description={toast.description}
        type={toast.type}
        button={
          toast.button && {
            label: toast.button.label,
            onClick: toast.button.onClick,
          }
        }
      />
    ),
    {
      duration: 5000,
      className: "right-0 z-[99]",
    },
  );
};

const Toast = ({ title, description, id, type }: ToastProps) => {
  const isError = type === "error";
  // A partial success: something was saved, but the caller has a caveat to report. Styled
  // apart from error so it does not read as "nothing happened".
  const isWarning = type === "warning";
  return (
    <output
      className={tw(
        "bg-lightGreen relative z-40 block items-center rounded-sm p-4 md:w-[486px]",
        isError && "bg-red-50",
        isWarning && "bg-lightYellow border-yellow border",
      )}
    >
      <div className="flex items-center gap-3">
        {icon(type)}

        <div className="flex flex-col gap-0.5">
          <p
            className={tw(
              "text-indigo text-sm font-medium",
              isError && "text-red-600",
            )}
          >
            {title}
          </p>
          {description && (
            <p
              className={tw(
                "text-dark-blue-gray mt-1 text-sm",
                isError && "text-red-500",
              )}
            >
              {isError ? "Error: " : ""}
              {description}
            </p>
          )}
        </div>
      </div>

      <button
        className="absolute right-1.5 top-1.5 cursor-pointer"
        onClick={() => {
          sonnerToast.dismiss(id);
        }}
      >
        <icons.XMarkIcon className="text-indigo h-5 w-5" />
      </button>
    </output>
  );
};

const icon = (type: ToastType) => {
  if (type === "success") {
    return <icons.CheckCircleIcon className="text-indigo h-5 w-5 shrink-0" />;
  }

  if (type === "warning") {
    return (
      <icons.ExclamationCircleIcon className="h-5 w-5 shrink-0 text-amber-500" />
    );
  }

  return (
    <icons.ExclamationCircleIcon className="h-5 w-5 shrink-0 text-red-500" />
  );
};
